import { Size } from '@ps/web-core-store';
import { StarsButton, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { ReactNode } from 'react';
import React, { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { ModalContext } from '@swf/components/StarsModal/Context';
import Body from '@swf/components/StarsModal/components/Body';
import IBIcon from '@swf/core/assets/casino/instant-bonus-icon-fill.svg';
import FSBIcon from '@swf/core/assets/icons/free-spins.svg';
import useRequest from '@swf/core/hooks/useRequest';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { Dispatch } from '@swf/core/store';
import type { StarsCoinReward } from '@swf/core/types/StarsCoinReward';
import { RewardType } from '@swf/core/types/StarsCoinReward';
import { encodeQuery } from '@swf/core/utils';

import {
  TXTCLI_Cancel,
  TXTCLI_Close,
  TXTCLI_CongratulationsX,
  TXTCLI_Error,
  TXTWCL_Buy_this_item,
  TXTWCL_XtitleXXBrandCoinXX_PriceXX_ti_ELL,
  TXTWCL_You_receivedX_N0
} from './CasinoBuyReward.messages.json';
import styles from './CasinoBuyReward.scss';

interface CasinoBuyRewardParams {
  title: string;
  buttonText: string;
  reward?: StarsCoinReward;
  success?: boolean;
}
const casinoFSB = {
  style: styles.fsb,
  icon: <FSBIcon className={styles.icon} />
};
const casinoInstant = { style: styles.ib, icon: <IBIcon className={styles.icon} /> };

const typeConfig: Record<RewardType, { style: string; icon: JSX.Element }> = {
  [RewardType.CasinoFSB]: casinoFSB,
  [RewardType.CasinoInstant]: casinoInstant,
  [RewardType.VegasFSB]: casinoFSB,
  [RewardType.VegasInstant]: casinoInstant
};

const CasinoBuyReward: React.FC = () => {
  const { reward = {} as StarsCoinReward, success } = useSearchParams<CasinoBuyRewardParams>();

  const request = useRequest();
  const history = useHistory();
  const { modal } = useDispatch<Dispatch>();

  const { setStyle } = useContext(ModalContext);

  useEffect(() => {
    setStyle({ size: Size.MEDIUM, className: styles.buyRewardModal });
  });

  const handleClick = () =>
    request({
      service: 'rewards.store.buy',
      body: { id: reward.id }
    }).subscribe(
      () =>
        history.push({
          pathname: '/casino/buy-reward',
          state: { reward, success: true },
          search: encodeQuery({ reward, success: true })
        }),
      (error) =>
        history.push({
          pathname: '/casino/error',
          state: { title: TXTCLI_Error, message: error.body.message },
          search: encodeQuery({ title: TXTCLI_Error, message: error.body.message })
        })
    );

  return (
    <Body
      className={classNames(
        styles.modalBody,
        typeConfig[reward.type].style,
        success && styles.completed,
        !success && styles.prompt
      )}
    >
      <div className={styles.gradient} />
      <div className={styles.fill} />
      {typeConfig[reward.type].icon}

      <div className={styles.titleBlock}>
        <div className={styles.title}>
          {success ? <Translation {...TXTCLI_CongratulationsX} /> : reward.name}
        </div>
        <div className={styles.subtitle}>
          {success ? (
            <Translation {...TXTWCL_You_receivedX_N0} values={{ 0: reward.name }} />
          ) : (
            <Translation
              {...TXTWCL_XtitleXXBrandCoinXX_PriceXX_ti_ELL}
              values={{
                0: reward.price,
                title: (...chunks: ReactNode[]) => <div>{chunks}</div>,
                subtitle: (msg: ReactNode) => <div>{msg}</div>
              }}
            />
          )}
        </div>
      </div>
      {success ? (
        <StarsButton onClick={() => modal.close()} translation={TXTCLI_Close} />
      ) : (
        <div className={styles.messageBlock}>
          <div className={styles.message}>{reward.description}</div>
          <div className={classNames(styles.buttons)}>
            <StarsButton
              onClick={() => (success ? modal.close() : handleClick())}
              translation={success ? TXTCLI_Close : TXTWCL_Buy_this_item}
            />
            {!success && (
              <StarsButton
                variant="text"
                onClick={() => modal.close()}
                translation={TXTCLI_Cancel}
              />
            )}
          </div>
        </div>
      )}
    </Body>
  );
};

export default CasinoBuyReward;
